import { RootState } from "@/app/stores"
import { getRegistrationStatistics } from "@/shared/api/community"
import { getCampaign, IListPrizesResult, IMarketingCampaing, getPrizes, IMarketingCampaignStatistics, getMarketingCampaignStatistics, getParticiptionsCount, listParticipations } from "@/shared/api/marketing-campaigns"
import { IListParticipationsResult } from "@/shared/api/marketing-campaigns/types"
import { ErrorType } from "@/shared/types"
import { createAsyncThunk } from "@reduxjs/toolkit"
import { AxiosError } from "axios"

export const fetchMarketingCampaign = createAsyncThunk<
    IMarketingCampaing,
    string,
    { state: RootState }
>('marketingCampaign/fetchMarketingCampaign', async (id, thunkAPI) => {
    try {
        const data = await getCampaign(id)
        return data.data
    } catch (err: unknown) {
        const knownError = err as AxiosError<ErrorType>

        return thunkAPI.rejectWithValue({
            message: knownError.response?.data?.message,
            error_id: knownError.response?.data?.error_id
        })
    }
})

export const fetchPrizes = createAsyncThunk<
    IListPrizesResult,
    void,
    { state: RootState }
>('marketingCampaign/fetchPrizes', async (_, thunkAPI) => {
    try {
        const { campaign, prizesPage, prizesLimit } = thunkAPI.getState().marketingCampaign
        const data = await getPrizes(campaign.id, prizesPage, prizesLimit)
        return data.data
    } catch (err: unknown) {
        const knownError = err as AxiosError<ErrorType>

        return thunkAPI.rejectWithValue({
            message: knownError.response?.data?.message,
            error_id: knownError.response?.data?.error_id
        })
    }
})

export const fetchMarketingCampaignStatistics = createAsyncThunk<
    IMarketingCampaignStatistics[],
    void,
    { state: RootState }
>('marketingCampaign/fetchMarketingCampaignStatistics', async (_, thunkAPI) => {
    try {
        const { campaign } = thunkAPI.getState().marketingCampaign
        const data = await getMarketingCampaignStatistics(campaign.id)
        return data.data
    } catch (err: unknown) {
        const knownError = err as AxiosError<ErrorType>

        return thunkAPI.rejectWithValue({
            message: knownError.response?.data?.message,
            error_id: knownError.response?.data?.error_id
        })
    }
})

export const fetchParticipationsCount = createAsyncThunk<
    number,
    void,
    { state: RootState }
>('marketingCampaign/fetchParticipationsCount', async (_, thunkAPI) => {
    try {
        const { campaign } = thunkAPI.getState().marketingCampaign
        const data = await getParticiptionsCount(campaign.id)
        return data.data.count
    } catch (err: unknown) {
        const knownError = err as AxiosError<ErrorType>

        return thunkAPI.rejectWithValue({
            message: knownError.response?.data?.message,
            error_id: knownError.response?.data?.error_id
        })
    }
})

export const fetchRegistrationsCount = createAsyncThunk<
    number,
    void,
    { state: RootState }
>('marketingCampaign/fetchRegistrationsCount', async (_, thunkAPI) => {
    try {
        const { campaign } = thunkAPI.getState().marketingCampaign
        const startDate = new Date(campaign.start_date)
        const endDate = new Date(campaign.end_date)
        const data = await getRegistrationStatistics(startDate, endDate)
        const count = Object.values(data.data).reduce((acc, curr) => acc += Number(curr), 0)
        return count
    } catch (err: unknown) {
        const knownError = err as AxiosError<ErrorType>

        return thunkAPI.rejectWithValue({
            message: knownError.response?.data?.message,
            error_id: knownError.response?.data?.error_id
        })
    }
})

export const fetchParticipations = createAsyncThunk<
    IListParticipationsResult,
    void,
    { state: RootState }
>('marketingCampaign/fetchParticipations', async (_, thunkAPI) => {
    try {
        const { campaign, participationsPage, participationsLimit } = thunkAPI.getState().marketingCampaign
        const data = await listParticipations(campaign.id, participationsPage, participationsLimit)
        return data.data
    } catch (err: unknown) {
        const knownError = err as AxiosError<ErrorType>

        return thunkAPI.rejectWithValue({
            message: knownError.response?.data?.message,
            error_id: knownError.response?.data?.error_id
        })
    }
})
